import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, Badge, ActionIcon } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, DataTable, EmptyState, Tabs } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { HelpButton } from '../../app/guides/HelpButton';
import { TabHelp } from '../../app/guides/TabHelp';
import { useBots } from '../../lib/bots';
import { flowErrorMessage } from '../../lib/flow-limit';
import { BotSwitcher } from '../../components/BotSwitcher';
import { formatDateTime, formatCount, type Indexed } from '../../lib/format';
import { Banner } from '../../components/panel';
import { FileUpload, type MediaValue } from '../../components/FileUpload';
import { openKbDocument } from './open-document';
import { Switch } from '@repletoryreach/sdk-ui/inputs';
import { StatusPill, type PillTone } from '../../components/panel';
import { isCatchAll, isKnowledgeAssistant, knowledgeAssistantDefinition, notifyTargets } from './assistant-template';
import { answerToHtml } from '../../lib/answer-html';
import { LiveSourcesCard } from './LiveSources';
import { WebsitesCard } from './Websites';
import { AccuracyCards } from './Accuracy';
import './knowledge.css';

type KbDoc = flows.KbDocument;

/** What the media service can turn into text, and so what the AI can read. */
const ACCEPT = ['application/pdf', 'text/plain', 'text/markdown', 'text/html', 'text/csv', '.pdf', '.txt', '.md', '.html', '.csv'];

/**
 * The knowledge base: the documents an AI node may answer from.
 *
 * "The LLM needs training" is, in practice, this page. A price list, an FAQ,
 * a policy: upload it, and any AI node that ticks it answers from its
 * contents (and says when the answer is not there). The search box shows what
 * the AI would find for a question, which is the fastest way to see whether a
 * document is enough.
 */
/**
 * The page's tabs (8 October 2026): it had become one long scroll of seven cards. A tab whose contents need the
 * business's attention (a document that could not be read, a failing website or live source, an open conflict)
 * shows how many.
 */
/** The passage a chatbot's bookings and requests facts arrive in has no document (16 October 2026). */
const NO_DOCUMENT = '00000000-0000-0000-0000-000000000000';
const TABS = ['documents', 'websites', 'live', 'try', 'conflicts', 'corrections', 'chatbot'] as const;
type Tab = (typeof TABS)[number];

export default function KnowledgePage() {
  const t = useT();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(rawTab ?? '') ? (rawTab as Tab) : 'documents';
  function choose(next: string) {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  }
  // The same queries the cards use, so the counts and the cards stay in step.
  const websitesQ = useQuery({ queryKey: ['kb-websites'], queryFn: () => flows.listKbWebsites() });
  const sourcesQ = useQuery({ queryKey: ['kb-sources'], queryFn: () => flows.listKbSources() });
  const conflictsQ = useQuery({ queryKey: ['kb-conflicts', 'open-count'], queryFn: () => flows.listKbConflicts('open') });
  const [query, setQuery] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteName, setPasteName] = useState('');
  const [pasteBody, setPasteBody] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searched, setSearched] = useState<string | null>(null);

  const docs = useQuery({
    queryKey: ['kb-documents'],
    queryFn: () => flows.listKbDocuments({ limit: 200 }),
    // The index worker takes queued documents one at a time, so the list keeps
    // itself current while any are still waiting and stops once none are.
    refetchInterval: (q) => ((q.state.data?.data ?? []).some((d) => flows.isKbDocumentPending(d)) ? 2500 : false)
  });
  const all = (docs.data?.data ?? []) as Indexed<KbDoc>[];
  const attention: Partial<Record<Tab, number>> = {
    documents: all.filter((d) => statusKey(d.status) === 'failed').length,
    websites: (websitesQ.data?.data ?? []).filter((w) => w.status === 'failing').length,
    live: (sourcesQ.data?.data ?? []).filter((x) => x.is_active && x.status === 'failing').length,
    conflicts: (conflictsQ.data?.data ?? []).length
  };
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? all.filter((d) => d.name.toLowerCase().includes(q)) : all;
  }, [all, query]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['kb-documents'] });
  };

  /*
   * Several files at once. Each upload reports as it completes, at its own
   * pace, so completed files are gathered for a moment and sent together, and
   * batches go one after another so the plan allowance is counted in order.
   * The service only queues them; the index worker indexes one at a time, and
   * the list above shows each move from Queued to Indexed.
   */
  const waiting = useRef<MediaValue[]>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chain = useRef<Promise<void>>(Promise.resolve());
  const [sending, setSending] = useState(0);
  const [refused, setRefused] = useState<flows.KbBatchRefusal[]>([]);

  useEffect(() => () => { if (flushTimer.current) clearTimeout(flushTimer.current); }, []);

  function queueUploaded(m: MediaValue) {
    waiting.current.push(m);
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushUploaded, 700);
  }

  function flushUploaded() {
    const items = waiting.current.splice(0);
    if (items.length === 0) return;
    setSending((n) => n + 1);
    chain.current = chain.current.then(async () => {
      try {
        const res = await flows.addKbDocuments(items.map((m) => ({ media_id: m.id, name: m.original_name ?? undefined })));
        refresh();
        if (res.data.accepted.length > 0) notifySuccess(t('knowledge.add.queued', { count: res.data.accepted.length }), t('knowledge.title'));
        if (res.data.refused.length > 0) setRefused((prev) => [...prev, ...res.data.refused]);
      } catch (e) {
        notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('knowledge.toast.addFailed'));
      } finally {
        setSending((n) => n - 1);
      }
    });
  }

  function refusalText(r: flows.KbBatchRefusal): string {
    const name = r.name ?? r.media_id;
    const known = ['PLAN_LIMIT_REACHED', 'ALREADY_ADDED', 'NO_TEXT', 'MEDIA_NOT_FOUND'];
    return known.includes(r.code)
      ? t(`knowledge.add.refused.${r.code}`, { name })
      : t('knowledge.add.refused.other', { name, message: r.message });
  }

  const retry = useMutation({
    mutationFn: (d: KbDoc) => flows.retryKbDocument(d.id),
    onSuccess: (_r, d) => {
      refresh();
      notifySuccess(t('knowledge.toast.retried', { name: d.name }), t('knowledge.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('knowledge.toast.retryFailed'))
  });

  const pendingCount = all.filter((d) => flows.isKbDocumentPending(d)).length;

  const paste = useMutation({
    mutationFn: () => flows.createKbDocument({ name: pasteName.trim(), body: pasteBody }),
    onSuccess: (res) => {
      refresh();
      setPasteOpen(false);
      setPasteName('');
      setPasteBody('');
      notifySuccess(t('knowledge.toast.added', { name: res.data.name, chunks: res.data.chunk_count }), t('knowledge.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('knowledge.toast.addFailed'))
  });

  const remove = useMutation({
    mutationFn: (d: KbDoc) => flows.deleteKbDocument(d.id),
    onSuccess: (_r, d) => {
      refresh();
      notifySuccess(t('knowledge.toast.removed', { name: d.name }), t('knowledge.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('knowledge.toast.removeFailed'))
  });

  const reindex = useMutation({
    mutationFn: () => flows.reindexKb(),
    onSuccess: (res) => {
      refresh();
      notifySuccess(t('knowledge.toast.reindexed', { documents: res.data.documents, chunks: res.data.chunks }), t('knowledge.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('knowledge.toast.reindexFailed'))
  });

  /**
   * Open what the AI reads (open-document.ts): the source file of a document ingested from the library, the text of
   * a pasted one. Conflicts and the coach open documents the same way.
   */
  async function openDocument(d: KbDoc) {
    try {
      await openKbDocument(d);
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('knowledge.title'));
    }
  }

  // The answer the assistant would give, and what it read.
  // Answered in the voice of the chatbot being looked at (the only one, for most).
  const { active: activeBot } = useBots();
  const search = useQuery({
    queryKey: ['kb-ask', searched, activeBot?.id ?? null],
    queryFn: () => flows.askKb(searched!, { botId: activeBot?.id }),
    enabled: !!searched
  });
  // Where the same message would go on a real channel: a flow that says what
  // it is for may start instead of this answer, or ride along as a button.
  const route = useQuery({
    queryKey: ['kb-ask-route', searched, activeBot?.id ?? null],
    queryFn: () => flows.testIntent(searched!, { botId: activeBot?.id }),
    enabled: !!searched,
    retry: false
  });
  const routed = route.data?.data;
  const routeLine = !routed || routed.action === 'none' ? null
    : routed.action === 'jump' ? t('knowledge.search.route.jump', { flow: routed.flow?.name ?? '' })
    : routed.action === 'keyword' ? t('knowledge.search.route.keyword', { flow: routed.flow?.name ?? '' })
    : t('knowledge.search.route.offer', { flow: routed.offers[0]?.name ?? '', label: routed.offers[0]?.button_label ?? routed.offers[0]?.name ?? '' });

  return (
    <Stack gap="lg">
      <PageHeader title={t('knowledge.title')} description={t('knowledge.description')} withDivider={false}
        actions={<HelpButton guide="automation" />} />

      <Banner tone="info">
        {t('knowledge.banner')} <Link to="/flows">{t('nav.flows')}</Link>
      </Banner>

      <div data-guide="knowledge-tabs">
        <Tabs value={tab} onChange={choose}>
          <Tabs.List>
            {TABS.map((x) => (
              <span key={x} className="rr-tabgroup">
                <Tabs.Tab value={x} rightSection={attention[x] ? (
                  <span className="rr-tabcount" aria-label={t('knowledge.tabs.attention', { count: attention[x] })}>{attention[x]}</span>
                ) : undefined}>
                  {t(`knowledge.tabs.${x}`)}
                </Tabs.Tab>
                <TabHelp guide="automation" titleKey={`knowledge.tabs.${x}`} bodyKey={`guide.automation.tab.knowledge.${x}`} />
              </span>
            ))}
          </Tabs.List>
        </Tabs>
      </div>

      {tab === 'documents' && (
        <div data-guide="knowledge-documents">
        <Stack gap="lg">
          {/* Add: a file, or pasted text. */}
          <Card withBorder data-guide="knowledge-add">
            <Stack gap="sm">
              <Title order={4}>{t('knowledge.add.title')}</Title>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('knowledge.add.hintMany')}</Text>
              <FileUpload
                accept={ACCEPT}
                maxSize={20 * 1024 * 1024}
                value={null}
                multiple
                withLibrary={false}
                withManagement={false}
                onChange={() => { /* every completed file arrives through onUploaded */ }}
                onUploaded={queueUploaded}
              />
              {sending > 0 && <Text role="caption" c="var(--rr-color-text-muted)">{t('knowledge.add.sending')}</Text>}
              {refused.length > 0 && (
                <Alert color="warning" role="status">
                  <Stack gap={4}>
                    <Text role="bodySmall" fw={650}>{t('knowledge.add.refusedTitle')}</Text>
                    {refused.map((r, i) => <Text key={`${r.media_id}-${i}`} role="caption">{refusalText(r)}</Text>)}
                    <Group justify="flex-end">
                      <Button size="xs" variant="subtle" onClick={() => setRefused([])}>{t('action.dismiss')}</Button>
                    </Group>
                  </Stack>
                </Alert>
              )}
              <Button size="xs" variant="subtle" onClick={() => setPasteOpen((v) => !v)}>
                {pasteOpen ? t('action.cancel') : t('knowledge.add.paste')}
              </Button>
              {pasteOpen && (
                <Stack gap="sm">
                  <TextInput
                    label={t('knowledge.add.name')}
                    value={pasteName}
                    onChange={(e) => setPasteName(e.currentTarget.value)}
                    placeholder={t('knowledge.add.namePlaceholder')}
                  />
                  <Textarea
                    label={t('knowledge.add.text')}
                    description={t('knowledge.add.textHint')}
                    value={pasteBody}
                    minRows={6}
                    onChange={(e) => setPasteBody(e.currentTarget.value)}
                  />
                  <Group justify="flex-end">
                    <Button size="sm" disabled={!pasteName.trim() || pasteBody.trim().length < 20} loading={paste.isPending} onClick={() => paste.mutate()}>
                      {t('knowledge.add.save')}
                    </Button>
                  </Group>
                </Stack>
              )}
            </Stack>
          </Card>


          <Card withBorder noPadding className="rr-panel">
            <div className="rr-panel__filters">
              <TextInput
                aria-label={t('knowledge.list.search')}
                placeholder={t('knowledge.list.search')}
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
              />
              <Tooltip label={t('knowledge.list.reindexHint')} position="top">
                <Button size="xs" variant="default" loading={reindex.isPending} onClick={() => reindex.mutate()}>
                  {t('knowledge.list.reindex')}
                </Button>
              </Tooltip>
            </div>
            {pendingCount > 0 && (
              <Alert color="info" role="status">{t('knowledge.list.pending', { count: formatCount(pendingCount) })}</Alert>
            )}
            {docs.error && <Alert color="error" role="alert">{(docs.error as Error).message}</Alert>}
            {!docs.isPending && rows.length === 0 && (
              <EmptyState title={t('knowledge.list.empty')} description={t('knowledge.list.emptyHint')} />
            )}
            {rows.length > 0 && (
              <DataTable<Indexed<KbDoc>>
                records={rows}
                idAccessor="id"
                columns={[
                  {
                    accessor: 'name',
                    title: t('common.name'),
                    render: (d) => (
                      <Group gap="xs" wrap="nowrap">
                        <Text role="bodySmall" fw={650}>{d.name}</Text>
                        {/* The stored copy of a live source: kept fresh from an address, not uploaded. */}
                        {d.live_source_id && <Tooltip label={t('knowledge.live.badgeHint')} position="top"><Badge size="xs" variant="light">{t('knowledge.live.badge')}</Badge></Tooltip>}
                        {/* A page of a website: read from its address, kept by reading the site again. */}
                        {d.website_id && <Tooltip label={d.source_url ?? t('knowledge.web.badgeHint')} position="top"><Badge size="xs" variant="light">{t('knowledge.web.badge')}</Badge></Tooltip>}
                      </Group>
                    )
                  },
                  {
                    accessor: 'status',
                    title: t('knowledge.list.status'),
                    render: (d) => {
                      const pill = <StatusPill tone={statusTone(d.status)}>{t(`knowledge.status.${statusKey(d.status)}`)}</StatusPill>;
                      // A failure says why on hover, in the words the worker wrote.
                      return d.status === 'failed' && d.error ? <Tooltip label={d.error} position="top">{pill}</Tooltip> : pill;
                    }
                  },
                  { accessor: 'chunk_count', title: t('knowledge.list.chunks'), render: (d) => formatCount(d.chunk_count) },
                  {
                    accessor: 'embedding_model',
                    title: t('knowledge.list.index'),
                    render: (d) => d.embedding_model
                      ? <Badge size="xs" variant="light">{d.embedding_model}</Badge>
                      : <Text role="caption" c="var(--rr-color-text-muted)">{t('knowledge.list.notIndexed')}</Text>
                  },
                  { accessor: 'created_at', title: t('common.created'), render: (d) => formatDateTime(d.created_at) },
                  {
                    accessor: 'actions',
                    title: t('common.actions'),
                    render: (d) => (
                      <Group gap={4} wrap="nowrap">
                      {d.status === 'failed' && (
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          aria-label={t('knowledge.list.retry', { name: d.name })}
                          title={t('knowledge.list.retry', { name: d.name })}
                          disabled={retry.isPending}
                          onClick={() => retry.mutate(d)}
                        >
                          <RetryIcon />
                        </ActionIcon>
                      )}
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        aria-label={t('knowledge.list.view', { name: d.name })}
                        title={d.source_media_id ? t('knowledge.list.viewFile') : t('knowledge.list.viewText')}
                        onClick={() => void openDocument(d)}
                      >
                        <EyeIcon />
                      </ActionIcon>
                      {!d.live_source_id && !d.website_id && (
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        color="error"
                        aria-label={t('knowledge.list.remove', { name: d.name })}
                        onClick={() => openConfirmModal({
                          title: t('knowledge.modal.removeTitle'),
                          children: <Text role="bodySmall">{t('knowledge.modal.removeBody', { name: d.name })}</Text>,
                          labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
                          confirmProps: { color: 'red' },
                          onConfirm: () => remove.mutate(d)
                        })}
                      >
                        <TrashIcon />
                      </ActionIcon>
                      )}
                      </Group>
                    )
                  }
                ]}
              />
            )}
            {rows.length > 0 && (
              <div className="rr-panel__foot">
                <span>{t('knowledge.list.showing', { count: formatCount(rows.length), total: formatCount(all.length) })}</span>
              </div>
            )}
          </Card>
        </Stack>
        </div>
      )}

      {tab === 'try' && (
        <div data-guide="knowledge-try">
        {/* Try: what would the AI find? */}
        <Card withBorder>
          <Stack gap="sm">
            <Title order={4}>{t('knowledge.search.title')}</Title>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('knowledge.search.hint')}</Text>
            <form onSubmit={(e) => { e.preventDefault(); if (searchQuery.trim()) setSearched(searchQuery.trim()); }}>
              <Group gap="xs" align="flex-end" wrap="nowrap">
                <TextInput
                  aria-label={t('knowledge.search.placeholder')}
                  placeholder={t('knowledge.search.placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  style={{ flex: 1 }}
                />
                <Button size="sm" type="submit" disabled={!searchQuery.trim()} loading={search.isFetching}>{t('knowledge.search.go')}</Button>
              </Group>
            </form>
            {search.error && <Alert color="error" role="alert">{(search.error as Error).message}</Alert>}
            {search.data && (
              <Stack gap={8}>
                <div className={`rr-knowledge__answer${search.data.data.unsure ? ' is-unsure' : ''}`}>
                  <Text role="caption" fw={700}>{search.data.data.bot_name}</Text>
                  {search.data.data.action
                    ? <Text role="bodySmall" data-testid="kb-action">{t(`knowledge.search.action.${search.data.data.action}`)}</Text>
                    : search.data.data.unsure
                    ? <Text role="bodySmall">{t('knowledge.search.unsure')}</Text>
                    : <div className="rr-knowledge__answer-body" dangerouslySetInnerHTML={{ __html: answerToHtml(search.data.data.answer ?? '') }} />}
                </div>
                {routeLine && <Text role="caption" c="var(--rr-color-text-muted)" data-testid="kb-route">{routeLine}</Text>}
                {search.data.data.snippets.length > 0 ? (
                  <details className="rr-knowledge__sources">
                    <summary>{t('knowledge.search.sources', { count: search.data.data.snippets.length })}</summary>
                    <Stack gap={6}>
                      {search.data.data.snippets.map((s, i) => (
                        <div key={i} className="rr-knowledge__snippet">
                          <Group justify="space-between" gap="xs">
                            <Text role="caption" fw={650}>{s.document_id === NO_DOCUMENT ? t('knowledge.search.bookingFacts') : s.document_name}</Text>
                            <Badge size="xs" variant="light">{Math.round(s.score * 100)}%</Badge>
                          </Group>
                          <Text role="caption" c="var(--rr-color-text-muted)">{s.body}</Text>
                        </div>
                      ))}
                    </Stack>
                  </details>
                ) : (
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('knowledge.search.none')}</Text>
                )}
                {search.data.data.provider === 'stub' && (
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('knowledge.search.stubHint')}</Text>
                )}
              </Stack>
            )}
          </Stack>
        </Card>
        </div>
      )}

      {tab === 'websites' && <div data-guide="knowledge-websites"><WebsitesCard /></div>}
      {tab === 'live' && <div data-guide="knowledge-live"><LiveSourcesCard /></div>}
      {/* Conflicting information and corrections (3 October 2026), each on its own tab. */}
      {tab === 'conflicts' && <div data-guide="knowledge-conflicts"><AccuracyCards show="conflicts" /></div>}
      {tab === 'corrections' && <div data-guide="knowledge-corrections"><AccuracyCards show="corrections" /></div>}
      {tab === 'chatbot' && <div data-guide="knowledge-chatbot"><AssistantCard documentCount={all.length} /></div>}
    </Stack>
  );
}

/**
 * "Answer questions automatically": the flow that answers any message from
 * the whole knowledge base and tells the team when it cannot. It is an
 * ordinary flow (marked by its trigger), so this card only switches it on and
 * off, sets where the team is reached when it is first created, and points to
 * the builder for everything else.
 */
function AssistantCard({ documentCount }: { documentCount: number }) {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [targets, setTargets] = useState({ email: '', whatsapp: '', webhook: '' });
  const list = useQuery({ queryKey: ['flows'], queryFn: () => flows.listFlows() });
  // "Answer automatically" is a flow, and a flow belongs to a chatbot: with
  // several chatbots each has its own, switched on and off here on its own.
  const { active: bot, owns } = useBots();
  const all = useMemo(() => (list.data?.data ?? []).filter(owns), [list.data, owns]);
  const assistant = all.find((f) => isKnowledgeAssistant(f.definition));
  const otherCatchAll = all.find((f) => f.id !== assistant?.id && f.is_active && isCatchAll(f.definition));
  const targetsNow = assistant ? notifyTargets(assistant.definition) : [];
  const anyTarget = Object.values(targets).some((v) => v.trim());

  const create = useMutation({
    mutationFn: () => flows.createFlow({
      name: t('knowledge.assistant.flowName'),
      definition: knowledgeAssistantDefinition(t, targets),
      is_active: true,
      bot_id: bot?.id
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['flows'] });
      notifySuccess(t('knowledge.assistant.created'), t('knowledge.assistant.title'));
    },
    onError: (e) => notifyError(flowErrorMessage(e, t), t('knowledge.assistant.title'))
  });
  const toggle = useMutation({
    mutationFn: (on: boolean) => flows.updateFlow(assistant!.id, { is_active: on }),
    onSuccess: (_r, on) => {
      qc.invalidateQueries({ queryKey: ['flows'] });
      notifySuccess(t('knowledge.assistant.updated', { state: on ? t('knowledge.assistant.on').toLowerCase() : t('knowledge.assistant.off').toLowerCase() }), t('knowledge.assistant.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('knowledge.assistant.title'))
  });

  const on = !!assistant?.is_active;
  return (
    <Card withBorder className="rr-knowledge__assistant">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            <Group gap="sm" align="center">
              <Title order={4}>{t('knowledge.assistant.title')}</Title>
              <StatusPill tone={on ? 'active' : 'paused'}>{on ? t('knowledge.assistant.on') : t('knowledge.assistant.off')}</StatusPill>
            </Group>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('knowledge.assistant.intro')}</Text>
            <BotSwitcher description={t('bots.switcher.knowledgeHint')} />
          </Stack>
          {assistant && (
            <Switch
              aria-label={on ? t('knowledge.assistant.disable') : t('knowledge.assistant.enable')}
              checked={on}
              disabled={toggle.isPending}
              onChange={(checked) => toggle.mutate(checked)}
            />
          )}
        </Group>

        {documentCount === 0 && <Alert color="warning">{t('knowledge.assistant.noDocuments')}</Alert>}
        {otherCatchAll && on && <Alert color="warning">{t('knowledge.assistant.otherCatchAll', { name: otherCatchAll.name })}</Alert>}

        {!assistant && !list.isPending && (
          <Stack gap="sm">
            <Text role="inputLabel">{t('knowledge.assistant.escalationTitle')}</Text>
            <div className="rr-knowledge__targets">
              <TextInput label={t('knowledge.assistant.email')} value={targets.email} placeholder={'ops@example.com' /* i18n-ignore: example address */} onChange={(e) => setTargets({ ...targets, email: e.currentTarget.value })} />
              <TextInput label={t('knowledge.assistant.whatsapp')} value={targets.whatsapp} placeholder={'+27 82 123 4567' /* i18n-ignore: example number */} onChange={(e) => setTargets({ ...targets, whatsapp: e.currentTarget.value })} />
              <TextInput label={t('knowledge.assistant.webhook')} value={targets.webhook} placeholder={'https://example.com/hooks/requests' /* i18n-ignore: example address */} onChange={(e) => setTargets({ ...targets, webhook: e.currentTarget.value })} />
            </div>
            <Group justify="space-between" align="center">
              <Text role="caption" c="var(--rr-color-text-muted)">{t('knowledge.assistant.escalationHint')}</Text>
              <Button size="sm" disabled={!anyTarget || documentCount === 0} loading={create.isPending} onClick={() => create.mutate()}>
                {t('knowledge.assistant.enable')}
              </Button>
            </Group>
          </Stack>
        )}

        {assistant && (
          <Group justify="space-between" align="center">
            <Text role="bodySmall">
              {targetsNow.length > 0
                ? <>{t('knowledge.assistant.notifies')} <strong>{targetsNow.join(' · ')}</strong></>
                : t('knowledge.assistant.escalationNone')}
            </Text>
            <Button size="xs" variant="default" onClick={() => nav(`/flows/${assistant.id}/builder`)}>{t('knowledge.assistant.openBuilder')}</Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

/** A document's status as the word the list shows. `ingested` predates the queue and means indexed. */
function statusKey(status: string): 'queued' | 'indexing' | 'indexed' | 'failed' {
  if (status === 'queued' || status === 'indexing' || status === 'failed') return status;
  return 'indexed';
}

function statusTone(status: string): PillTone {
  switch (statusKey(status)) {
    case 'queued': return 'neutral';
    case 'indexing': return 'submitted';
    case 'failed': return 'rejected';
    default: return 'active';
  }
}

function RetryIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9M13.5 2.5v3.2h-3.2" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1.5 8s2.4-4.5 6.5-4.5S14.5 8 14.5 8s-2.4 4.5-6.5 4.5S1.5 8 1.5 8z" /><circle cx="8" cy="8" r="2" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2.8 4.4h10.4M6.4 4.4V3.1h3.2v1.3M4.2 4.4l.6 8.5h6.4l.6-8.5" />
    </svg>
  );
}
